import { invisiblyApi } from 'src/redux/api/invisibly-api';
import { InvisiblyApiResponse } from 'src/services/api/api-response.types';
import { APITagType } from 'src/redux/api/invisibly-api.types';

export type ReportingApiKeyStatus = 'active' | 'revoked';

export interface ReportingApiKeyScope {
  sources_count: number;
  accounts_count: number;
  campaigns_count: number;
}

export interface ReportingApiKey {
  id: string;
  name: string;
  client_id: string;
  client_title: string;
  masked_key: string;
  created_by_name: string | null;
  created_by_email: string | null;
  created_at: string;
  last_used_at: string | null;
  status: ReportingApiKeyStatus;
  is_never_used: boolean;
}

export interface ReportingApiKeyWithSecret extends ReportingApiKey {
  key: string;
  scope: ReportingApiKeyScope;
}

export interface ReportingApiKeyRevealResult {
  key: string;
}

const reportingApiKeysEndpoints = invisiblyApi.injectEndpoints({
  endpoints: (builder) => ({
    getReportingApiKeys: builder.query<ReportingApiKey[], string | void>({
      query: (clientId) => ({
        method: 'get',
        url: 'b2b/operations/reporting-api-keys',
        ...(clientId ? { params: { client_id: clientId } } : {}),
      }),
      transformResponse(response: InvisiblyApiResponse<ReportingApiKey[]>) {
        return response.item ?? [];
      },
      providesTags: [{ id: 'LIST', type: APITagType.ReportingApiKeys }],
    }),
    createReportingApiKey: builder.mutation<
    ReportingApiKeyWithSecret,
    { ext_client_id: string; name: string }
    >({
      query: (data) => ({
        method: 'post',
        url: 'b2b/operations/reporting-api-keys',
        data,
      }),
      transformResponse(response: InvisiblyApiResponse<ReportingApiKeyWithSecret>) {
        return response.item;
      },
      invalidatesTags: [
        { id: 'LIST', type: APITagType.ReportingApiKeys },
        { type: APITagType.ReportingApiUsage },
      ],
    }),
    revealReportingApiKey: builder.mutation<ReportingApiKeyRevealResult, string>({
      query: (keyId) => ({
        method: 'post',
        url: `b2b/operations/reporting-api-keys/${keyId}/reveal`,
      }),
      transformResponse(response: InvisiblyApiResponse<ReportingApiKeyRevealResult>) {
        return response.item;
      },
    }),
    revokeReportingApiKey: builder.mutation<ReportingApiKey, string>({
      query: (keyId) => ({
        method: 'post',
        url: `b2b/operations/reporting-api-keys/${keyId}/revoke`,
      }),
      transformResponse(response: InvisiblyApiResponse<ReportingApiKey>) {
        return response.item;
      },
      invalidatesTags: [
        { id: 'LIST', type: APITagType.ReportingApiKeys },
        { type: APITagType.ReportingApiUsage },
      ],
    }),
    rotateReportingApiKey: builder.mutation<ReportingApiKeyWithSecret, string>({
      query: (keyId) => ({
        method: 'post',
        url: `b2b/operations/reporting-api-keys/${keyId}/rotate`,
      }),
      transformResponse(response: InvisiblyApiResponse<ReportingApiKeyWithSecret>) {
        return response.item;
      },
      invalidatesTags: [
        { id: 'LIST', type: APITagType.ReportingApiKeys },
        { type: APITagType.ReportingApiUsage },
      ],
    }),
  }),
  overrideExisting: true,
});

export const {
  useGetReportingApiKeysQuery,
  useCreateReportingApiKeyMutation,
  useRevealReportingApiKeyMutation,
  useRevokeReportingApiKeyMutation,
  useRotateReportingApiKeyMutation,
} = reportingApiKeysEndpoints;
